/**
 * Centralized Node Type Registry
 *
 * Maps 16 primitive workflow types to React components.
 * Domain-agnostic: components render data from database, no hardcoded business logic.
 * Extensible: supports custom node renderers via configuration.
 */

import { Badge, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import {
  IconActivity,
  IconAdjustments,
  IconAlertTriangle,
  IconBolt,
  IconBrain,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconClock,
  IconDatabase,
  IconFileText,
  IconGitBranch,
  IconNetwork,
  IconPlaylistAdd,
  IconShield,
  IconTargetArrow,
  IconUser,
} from '@tabler/icons-react';
import React from 'react';
import { NodeProps } from 'reactflow';

import { BaseNodeData, ExecutionStatus, PrimitiveWorkflowType } from './types';

/**
 * Icon mapping for 16 primitive workflow types
 * Data-driven: can be overridden via node.data.renderConfig.icon
 */
export const PRIMITIVE_TYPE_ICONS: Record<PrimitiveWorkflowType, React.ComponentType<any>> = ({
  WorkflowDefinition: IconGitBranch,
  ActorDescriptor: IconUser,
  TransitionCatalog: IconPlaylistAdd,
  Policy: IconShield,
  Context: IconAdjustments,
  Schedule: IconCalendar,
  Resource: IconDatabase,
  Integration: IconNetwork,
  Instance: IconActivity,
  Signal: IconBolt,
  Task: IconTargetArrow,
  JobStatus: IconChartBar,
  ExecutionResult: IconCheck,
  TimelineItem: IconClock,
  Escalation: IconAlertTriangle,
  OptimizationRecommendation: IconBrain,
} as unknown as Record<PrimitiveWorkflowType, React.ComponentType<any>>);

/**
 * Color mapping for execution status
 * Data-driven: can be overridden via node.data.renderConfig.color
 */
export const STATUS_COLORS: Record<ExecutionStatus, string> = {
  [ExecutionStatus.Pending]: '#94a3b8',
  [ExecutionStatus.Running]: '#f59e0b',
  [ExecutionStatus.Completed]: '#10b981',
  [ExecutionStatus.Failed]: '#ef4444',
  [ExecutionStatus.Skipped]: '#9ca3af',
  [ExecutionStatus.TimedOut]: '#dc2626',
  [ExecutionStatus.RequiresHuman]: '#8b5cf6',
  [ExecutionStatus.Paused]: '#06b6d4',
  [ExecutionStatus.Cancelled]: '#6b7280',
};

/**
 * Default Node Component
 *
 * Renders any primitive workflow type in a standard way.
 * All styling/behavior driven by node.data from database.
 * Life-critical: displays errors, metrics, compliance flags.
 */
export function DefaultNode(props: NodeProps<BaseNodeData>) {
  const data: BaseNodeData = props.data;
  const selected = props.selected;
  const complianceFlags: string[] = data.complianceFlags ?? [];
  const IconComponent = PRIMITIVE_TYPE_ICONS[data.type] || IconFileText;

  // Determine colors from data (data-driven)
  const statusColor = data.status ? STATUS_COLORS[data.status] : undefined;
  const borderColor = data.renderConfig?.color || statusColor || '#cbd5e1';
  const hasErrors = (data.errors?.length ?? 0) > 0;
  const hasWarnings = data.errors?.some((e) => e.severity === 'warning') ?? false;

  // Get display configuration from data
  const showMetrics = data.renderConfig?.showMetrics ?? false;
  const showErrors = data.renderConfig?.showErrors ?? true;

  return (
    <Card
      withBorder
      radius="md"
      shadow={selected ? 'md' : 'sm'}
      style={{
        minWidth: 220,
        maxWidth: 300,
        backgroundColor: 'var(--mantine-color-body)',
        borderColor: selected ? 'var(--mantine-color-blue-5)' : borderColor,
        borderWidth: selected ? 2 : 1,
        opacity: data.status === ExecutionStatus.Skipped ? 0.6 : 1,
      }}
      className={data.renderConfig?.customClassName}
    >
      <Stack gap={6}>
        {/* Header with icon and title */}
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon
              size="md"
              radius="md"
              color={statusColor ? undefined : 'gray'}
              style={{ backgroundColor: statusColor }}
            >
              <IconComponent size={18} />
            </ThemeIcon>
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm" truncate title={data.label}>
                {data.label}
              </Text>
              <Text size="xs" c="dimmed">
                {data.type}
              </Text>
            </Stack>
          </Group>

          {/* Status/Error badges */}
          <Group gap={4}>
            {hasErrors && showErrors && (
              <Badge color="red" size="sm" variant="filled">
                {data.errors!.filter((e) => e.severity === 'error' || !e.severity).length}
              </Badge>
            )}
            {hasWarnings && showErrors && (
              <Badge color="yellow" size="sm" variant="light">
                {data.errors!.filter((e) => e.severity === 'warning').length}
              </Badge>
            )}
            {data.renderConfig?.badge && (
              <Badge size="sm" variant="light">
                {data.renderConfig.badge}
              </Badge>
            )}
          </Group>
        </Group>

        {/* Description (if provided from database) */}
        {data.description && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {data.description}
          </Text>
        )}

        {/* Compliance flags (life-critical) */}
        {complianceFlags.length > 0 ? (
          <Group gap={4}>
            {complianceFlags.slice(0, 3).map((flag: string) => (
              <Badge key={flag} size="xs" color="grape" variant="dot">
                {flag}
              </Badge>
            ))}
          </Group>
        ) : null}

        {/* Metrics (if enabled from data) */}
        {showMetrics && data.metrics && (
          <Stack gap={2}>
            {data.metrics.executionTime !== undefined && (
              <Text size="xs" c="dimmed">
                ⏱️ {data.metrics.executionTime}ms
              </Text>
            )}
            {data.metrics.successCount !== undefined && (
              <Text size="xs" c="dimmed">
                ✓ {data.metrics.successCount} / ✗ {data.metrics.errorCount ?? 0}
              </Text>
            )}
          </Stack>
        )}

        {/* Actor context (actor-first architecture) */}
        {data.actorContext?.actorName && (
          <Badge size="xs" color="cyan" variant="light" leftSection={<IconUser size={10} />}>
            {data.actorContext.actorName}
          </Badge>
        )}

        {/* Show assignee from metadata (for Tasks) */}
        {data.metadata?.assignee != null && (
          <Badge size="xs" color="cyan" variant="light" leftSection={<IconUser size={10} />}>
            {String(data.metadata.assignee).replace('role_', '').replace(/_/g, ' ')}
          </Badge>
        )}

        {/* Audit version (life-critical tracking) */}
        {data.auditInfo?.version && (
          <Text size="xs" c="dimmed">
            v{data.auditInfo.version}
          </Text>
        )}
      </Stack>
    </Card>
  );
};

/**
 * Compact Node Component
 *
 * Minimal representation for high-density canvases.
 * Still data-driven from database.
 */
export const CompactNode: React.FC<NodeProps<BaseNodeData>> = ({ data, selected }) => {
  const IconComponent = PRIMITIVE_TYPE_ICONS[data.type] || IconFileText;
  const statusColor = data.status ? STATUS_COLORS[data.status] : '#cbd5e1';
  const hasErrors = (data.errors?.length ?? 0) > 0;

  return (
    <Card
      withBorder
      radius="md"
      shadow={selected ? 'sm' : 'xs'}
      p="xs"
      style={{
        minWidth: 120,
        backgroundColor: 'var(--mantine-color-body)',
        borderColor: selected ? 'var(--mantine-color-blue-5)' : statusColor,
        borderWidth: selected ? 2 : 1,
      }}
      className={data.renderConfig?.customClassName}
    >
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size="sm" radius="md" style={{ backgroundColor: statusColor }}>
          <IconComponent size={14} />
        </ThemeIcon>
        <Text fw={600} size="xs" truncate style={{ flex: 1 }} title={data.label}>
          {data.label}
        </Text>
        {hasErrors && (
          <Badge color="red" size="xs" circle>
            {data.errors!.length}
          </Badge>
        )}
      </Group>
    </Card>
  );
};

/**
 * Node Type Registry
 *
 * Maps primitive types to React components.
 * Extensible: can register custom components for specific types.
 */
class NodeTypeRegistry {
  private registry = new Map<string, React.ComponentType<NodeProps<BaseNodeData>>>();

  constructor() {
    // Register default component for all 16 primitive types
    Object.keys(PRIMITIVE_TYPE_ICONS).forEach((type) => {
      this.registry.set(type, DefaultNode);
    });

    // Register compact variant
    this.registry.set('compact', CompactNode);
  }

  /**
   * Register a custom component for a primitive type
   */
  register(
    type: PrimitiveWorkflowType | string,
    component: React.ComponentType<NodeProps<BaseNodeData>>,
  ): void {
    this.registry.set(type, component);
  }

  /**
   * Get component for a type (falls back to default)
   */
  get(type: string): React.ComponentType<NodeProps<BaseNodeData>> {
    return this.registry.get(type) || DefaultNode;
  }

  /**
   * Get all registered types as React Flow nodeTypes object
   */
  getNodeTypes(): Record<string, React.ComponentType<NodeProps<BaseNodeData>>> {
    const nodeTypes: Record<string, React.ComponentType<NodeProps<BaseNodeData>>> = {};

    this.registry.forEach((component, type) => {
      nodeTypes[type] = component;
    });

    return nodeTypes;
  }
}

/**
 * Singleton instance
 */
export const nodeRegistry = new NodeTypeRegistry();

/**
 * Hook to get node types (for use in React Flow)
 * Allows custom node types to be merged with default types
 */
export function useNodeTypes(
  customTypes?: Record<string, React.ComponentType<NodeProps<BaseNodeData>>>,
): Record<string, React.ComponentType<NodeProps<BaseNodeData>>> {
  return React.useMemo(() => {
    const defaultTypes = nodeRegistry.getNodeTypes();
    return customTypes ? { ...defaultTypes, ...customTypes } : defaultTypes;
  }, [customTypes]);
}
